import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import { formatAppDate } from '../../utils/dateUtils';

/**
 * RequisitionDetailPanel — slide-in panel showing a requisition's full delivery chain.
 *
 * Usage:
 *   <RequisitionDetailPanel requisitionId={123} onClose={() => setPanelOpen(false)} />
 *
 * Shows:
 *   - Requisition header: number, status, requested_by, approved_by, dates
 *   - Items table: item_code, item_name, requested_qty, approved_qty, issued_qty
 *   - Linked pick list(s): number, picker, completed date, status
 */

const RequisitionDetailPanel = ({ requisitionId, onClose }) => {
  const { t } = useTranslation('warehouse');
  const [requisition, setRequisition] = useState(null);
  const [pickLists, setPickLists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!requisitionId) return;
    setLoading(true);
    Promise.all([
      apiFetch(getApiUrl(`/api/inventory-requisitions/${requisitionId}`))
        .then(r => r.ok ? r.json() : null),
      apiFetch(getApiUrl(`/api/pick-lists?requisition_id=${requisitionId}`))
        .then(r => r.ok ? r.json() : [])
    ])
      .then(([req, pls]) => {
        setRequisition(req);
        setPickLists(Array.isArray(pls) ? pls : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [requisitionId]);

  if (!requisitionId) return null;

  return (
    <div className="slide-panel-overlay" onClick={onClose}>
      <div className="slide-panel" onClick={e => e.stopPropagation()}>
        <div className="slide-panel-header">
          <h3>{t('requisitionDetailPanel.title')}</h3>
          <button className="slide-panel-close" onClick={onClose}>✕</button>
        </div>

        <div className="slide-panel-body">
          {loading ? (
            <div className="loading" style={{ padding: '2rem' }}>{t('common:ui.loading')}</div>
          ) : !requisition ? (
            <div style={{ padding: '2rem', color: 'var(--text-secondary)' }}>{t('requisitionDetailPanel.notFound')}</div>
          ) : (
            <>
              {/* Header */}
              <div className="requisition-panel-header">
                <div className="requisition-panel-meta">
                  <div><strong>{requisition.requisition_number}</strong></div>
                  <div>
                    <span className={`status-badge status-${requisition.status?.toLowerCase()}`}>
                      {t(`returnsAndRequisitions.status.${requisition.status}`, { defaultValue: requisition.status })}
                    </span>
                  </div>
                </div>
                <div className="requisition-panel-info-grid">
                  <div><span className="info-label">{t('requisitionDetailPanel.fields.po')}</span><span>{requisition.po_number || '-'}</span></div>
                  <div><span className="info-label">{t('requisitionDetailPanel.fields.task')}</span><span>{requisition.task_name || '-'}</span></div>
                  <div><span className="info-label">{t('requisitionDetailPanel.fields.requestedBy')}</span><span>{requisition.requested_by_name || '-'}</span></div>
                  <div><span className="info-label">{t('requisitionDetailPanel.fields.approvedBy')}</span><span>{requisition.approved_by_name || '-'}</span></div>
                  <div><span className="info-label">{t('common:ui.date')}</span><span>{requisition.requested_date ? formatAppDate(requisition.requested_date) : '-'}</span></div>
                </div>
              </div>

              {/* Items */}
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('requisitionDetailPanel.items')}</h4>
                <table className="data-table" style={{ width: '100%', fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th>{t('common:ui.code')}</th>
                      <th>{t('common:ui.material')}</th>
                      <th>{t('returnsAndRequisitions.columns.requested')}</th>
                      <th>{t('returnsAndRequisitions.columns.approved')}</th>
                      <th>{t('returnsAndRequisitions.columns.issued')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(requisition.items || []).map((item, i) => (
                      <tr key={item.requisition_item_id || i}>
                        <td>{item.item_code}</td>
                        <td>{item.item_name}</td>
                        <td>{parseFloat(item.requested_quantity || 0).toFixed(3)}</td>
                        <td>{item.approved_quantity != null ? parseFloat(item.approved_quantity).toFixed(3) : '-'}</td>
                        <td>{item.issued_quantity != null ? parseFloat(item.issued_quantity).toFixed(3) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pick Lists */}
              <div style={{ marginTop: '1.25rem' }}>
                <h4 style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {t('requisitionDetailPanel.pickLists', { count: pickLists.length })}
                </h4>
                {pickLists.length === 0 ? (
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{t('requisitionDetailPanel.noPickList')}</p>
                ) : (
                  pickLists.map(pl => (
                    <div key={pl.pick_list_id} style={{ border: '1px solid var(--border)', borderRadius: '6px', padding: '0.75rem', marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                        <strong style={{ fontSize: '0.9rem' }}>{pl.pick_list_number}</strong>
                        <span className={`status-badge status-${pl.status?.toLowerCase()}`}>{t(`returnsAndRequisitions.status.${pl.status}`, { defaultValue: pl.status })}</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                        {pl.assigned_to_name && <span>{t('requisitionDetailPanel.picker', { name: pl.assigned_to_name })}</span>}
                        {pl.completed_date && <span>{t('requisitionDetailPanel.completed', { date: formatAppDate(pl.completed_date) })}</span>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequisitionDetailPanel;
